import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Building2, 
  Trash2, 
  Plus, 
  Minus, 
  X, 
  MapPin, 
  Calendar, 
  ShoppingBag, 
  ChevronRight 
} from 'lucide-react';
import MobileCheckoutForm from './MobileCheckoutForm';

const MobileOrderModal = ({ 
  isOpen, 
  onClose, 
  orderItems, 
  handleUpdateQuantity,
  onRemoveItem,
  restaurantInfo = {
    name: "Mr. WoodFire",
    address: "55-61 Railway Avenue Werribee Victoria"
  }
}) => {
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const calculateSubtotal = () => {
    return orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const gstTax = calculateSubtotal() * 0.10;
  const total = calculateSubtotal() + gstTax;
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handlePlaceOrder = () => {
    setIsCheckoutOpen(true);
  };

  const handleCloseCheckout = () => {
    setIsCheckoutOpen(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] md:hidden">
        {/* Overlay */}
        <div 
          className="absolute inset-0 transition-opacity duration-300 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal content */}
        <div className="absolute inset-0 flex flex-col bg-white animate-slide-up">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-amber-100">
            <button 
              onClick={onClose} 
              className="p-2 transition-colors rounded-full text-amber-500 hover:bg-amber-50"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-amber-900">Your Order</h2>
            <div className="px-4 py-1.5 bg-white rounded-full text-sm font-medium text-amber-700 shadow-md border border-amber-100">
              Pickup
            </div>
          </div>
              
          {/* Restaurant Info Card */}
          <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="p-4 bg-white border shadow-md rounded-xl border-amber-100">
              {/* Restaurant Info */}
              <div className="flex items-start gap-3">
                <Building2 className="flex-shrink-0 w-5 h-5 mt-1 text-amber-500" />
                <div>
                  <h3 className="font-medium text-amber-900">{restaurantInfo.name}</h3>
                  <div className="flex items-center mt-1 text-sm text-amber-600">
                    <MapPin className="w-4 h-4 mr-1" />
                    {restaurantInfo.address}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px my-4 bg-amber-100" />

              {/* Pickup Time */}
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <Clock className="flex-shrink-0 w-5 h-5 mt-1 text-amber-500" />
                  <div>
                    <div className="flex items-center text-sm text-amber-600">
                      <Calendar className="w-4 h-4 mr-1" />
                      Today
                    </div>
                    <p className="font-medium text-amber-900">Ready in 15-20 mins</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Items Section */}
          <div className="flex-1 overflow-auto">
            <div className="p-4">
              <h3 className="mb-4 font-bold text-amber-900">Order Items</h3>

              {orderItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-amber-100">
                    <ShoppingBag className="w-8 h-8 text-amber-500" />
                  </div>
                  <p className="text-lg font-medium text-amber-900">Your order is empty</p>
                  <p className="mt-2 text-sm text-amber-600">
                    Add some delicious items to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orderItems.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex gap-3 p-3 bg-white border rounded-xl border-amber-100"
                    >
                      <img 
                        src={item.imageUrl} 
                        alt={item.label} 
                        className="flex-shrink-0 object-cover w-20 h-20 rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-amber-900">{item.label}</h4>
                          <button 
                            onClick={() => onRemoveItem(item.id)}
                            className="p-1 transition-colors rounded-full text-amber-400 hover:bg-amber-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center bg-white border rounded-full shadow-sm border-amber-200">
                            <button 
                              onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                              className="p-1.5 hover:bg-amber-50 rounded-full transition-colors"
                            >
                              <Minus className="w-4 h-4 text-amber-600" />
                            </button>
                            <span className="w-8 font-medium text-center text-amber-900">{item.quantity}</span>
                            <button 
                              onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                              className="p-1.5 hover:bg-amber-50 rounded-full transition-colors"
                            >
                              <Plus className="w-4 h-4 text-amber-600" />
                            </button>
                          </div>
                          <span className="font-medium text-amber-900">${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          {orderItems.length > 0 && (
            <div className="bg-white border-t border-amber-100">
              <div className="p-4 space-y-3">
                {/* Subtotal */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-600">Subtotal</span>
                  <span className="text-amber-900">${calculateSubtotal().toFixed(2)}</span>
                </div>

                {/* Service Fee */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-600">GST (10%)</span>
                  <span className="text-amber-900">${gstTax.toFixed(2)}</span>
                </div>

                {/* Divider */}
                <div className="h-px bg-amber-100" />

                {/* Total */}
                <div className="flex items-center justify-between">
                  <span className="font-bold text-amber-900">Total</span>
                  <span className="font-bold text-amber-900">${total.toFixed(2)}</span>
                </div>

                {/* Checkout Button */}
                <button 
                  onClick={handlePlaceOrder}
                  className="flex items-center justify-center w-full px-6 py-3 font-medium text-white transition-colors rounded-xl bg-amber-600 hover:bg-amber-700"
                >
                  Place Order
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Checkout Form */}
      <MobileCheckoutForm
        isOpen={isCheckoutOpen}
        onClose={handleCloseCheckout}
        orderItems={orderItems}
        restaurantInfo={restaurantInfo}
      />
    </>
  );
};

export default MobileOrderModal;